import { useState, useEffect } from 'react';
import type { StorageKey } from '../constants/storage';

/**
 * useLocalStorage - Hook for managing localStorage with React state
 *
 * Provides automatic synchronization between localStorage and component state,
 * including cross-tab synchronization via the storage event.
 *
 * ## WHEN TO USE:
 * - Components that need persistent state across page reloads
 * - When changes should trigger re-renders
 * - When cross-tab synchronization is desired
 * - User preferences that need to be saved (favorites, settings, etc.)
 *
 * ## WHEN NOT TO USE (acceptable exceptions):
 * - **Authentication tokens** - Use AuthContext (getIdToken) (security-sensitive)
 * - **Services/APIs** - Storage services like imageHistoryStorage.ts that
 *   provide non-reactive persistence (no component re-renders needed)
 * - **Contexts** - Contexts that manage their own storage lifecycle
 *   (ThemeContext, SettingsContext) with custom update logic
 * - **i18n** - StorageService.ts for language preferences (service layer)
 * - **One-time reads** - Values only read once on mount (migrations, etc.)
 * - **Cache with TTL** - Data with expiration logic (use custom caching)
 *
 * ## BEST PRACTICES:
 * - ALWAYS use STORAGE_KEYS constants for keys (from constants/storage.ts)
 * - NEVER use string literals for storage keys
 * - Use this hook in components, not in services
 *
 * @param key - The localStorage key to use (prefer STORAGE_KEYS constants)
 * @param initialValue - The initial value if nothing is in localStorage
 * @returns [value, setValue] - Similar to useState but persisted
 *
 * @example
 * ```tsx
 * import { useLocalStorage } from '../hooks/useLocalStorage';
 * import { STORAGE_KEYS } from '../constants/storage';
 *
 * function MyComponent() {
 *   const [favorites, setFavorites] = useLocalStorage<string[]>(
 *     STORAGE_KEYS.FOOOCUS_FAVORITES,
 *     []
 *   );
 *
 *   const toggleFavorite = (id: string) => {
 *     setFavorites(prev =>
 *       prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]
 *     );
 *   };
 * }
 * ```
 */
export function useLocalStorage<T>(
  key: StorageKey | string,
  initialValue: T
): [T, (value: T | ((val: T) => T)) => void] {
  // Get initial value from localStorage or use provided initial value
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(`Error loading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  // Update localStorage when value changes
  const setValue = (value: T | ((val: T) => T)) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore = value instanceof Function ? value(storedValue) : value;

      setStoredValue(valueToStore);

      // Save to localStorage
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.error(`Error saving localStorage key "${key}":`, error);
    }
  };

  // Listen for changes in other tabs/windows
  useEffect(() => {
    const handleStorageChange = (error: StorageEvent) => {
      if (error.key === key && error.newValue !== null) {
        try {
          setStoredValue(JSON.parse(error.newValue));
        } catch (error) {
          console.error(`Error parsing localStorage change for key "${key}":`, error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [key]);

  return [storedValue, setValue];
}

/**
 * useFormConfigPersistence - Hook for form configuration persistence
 *
 * Specialized variant of useLocalStorage for form state that automatically
 * omits certain fields from persistence (like user-entered topic text).
 *
 * ## USE CASE:
 * - Forms with configuration that should persist between sessions
 * - Settings forms where some fields are sensitive/transient
 *
 * @param storageKey - The localStorage key to use
 * @param initialConfig - The initial configuration object
 * @param fieldsToOmit - Fields to exclude from persistence (default: ['topic'])
 */
export function useFormConfigPersistence<T extends object>(
  storageKey: StorageKey | string,
  initialConfig: T,
  fieldsToOmit: (keyof T & string)[] = ['topic' as keyof T & string]
): {
  config: T;
  updateConfig: (updates: Partial<T>) => void;
  resetConfig: () => void;
} {
  // Load saved config, omitting specified fields
  const getSavedConfig = (): Partial<T> => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsedConfig = JSON.parse(saved);
        // Remove fields that shouldn't be persisted
        fieldsToOmit.forEach((field) => delete parsedConfig[field]);
        return parsedConfig;
      }
    } catch (error) {
      console.error('Failed to load saved config:', error);
    }
    return {};
  };

  const savedConfig = getSavedConfig();

  // Merge saved config with initial config
  const [config, setConfig] = useState<T>(() => ({
    ...initialConfig,
    ...savedConfig,
    // Ensure omitted fields use initial values
    ...fieldsToOmit.reduce(
      (acc, field) => ({
        ...acc,
        [field]: initialConfig[field],
      }),
      {}
    ),
  }));

  // Update config and save to localStorage
  const updateConfig = (updates: Partial<T>) => {
    setConfig((current) => {
      const newConfig = { ...current, ...updates };

      // Save to localStorage (excluding omitted fields)
      const configToSave = { ...newConfig };
      fieldsToOmit.forEach((field) => delete configToSave[field]);

      try {
        localStorage.setItem(storageKey, JSON.stringify(configToSave));
      } catch (error) {
        console.error('Failed to save config:', error);
      }

      return newConfig;
    });
  };

  // Reset to initial config and clear localStorage
  const resetConfig = () => {
    setConfig(initialConfig);
    try {
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.error('Failed to clear saved config:', error);
    }
  };

  return { config, updateConfig, resetConfig };
}
